import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, ShopError, type Customer } from '../api';

/**
 * Stav přihlášeného zákazníka.
 *
 * Session drží server v httpOnly cookie (`zoo_customer_session`) – JS ji
 * nevidí a ani vidět nemá. Kontext si při načtení JEDNOU zavolá /me a podle
 * odpovědi ví, jestli je někdo přihlášený. Po přihlášení, registraci i
 * odhlášení stav rovnou aktualizuje z odpovědi serveru (žádné hádání).
 */
interface CustomerContextValue {
  /** Přihlášený zákazník, nebo null. `undefined` dokud se /me nenačte. */
  readonly customer: Customer | null | undefined;
  /** true, dokud první /me doběhne – aby UI neproblikávalo. */
  readonly loading: boolean;
  login(email: string, password: string): Promise<void>;
  register(input: { email: string; password: string; fullName?: string; phone?: string }): Promise<void>;
  logout(): Promise<void>;
  /** Vynutí opětovné načtení stavu z /me. */
  refresh(): Promise<void>;
}

const CustomerContext = createContext<CustomerContextValue | null>(null);

export function CustomerProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<Customer | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const result = await api.me();
      setCustomer(result.customer);
    } catch {
      // Když /me selže (síť, server), bereme jako nepřihlášeného – výloha
      // funguje dál i bez účtu.
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Načtení stavu jednou při startu.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await api.loginCustomer(email, password);
    setCustomer(result.customer);
  }, []);

  const register = useCallback(
    async (input: { email: string; password: string; fullName?: string; phone?: string }) => {
      const result = await api.registerCustomer(input);
      setCustomer(result.customer);
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api.logoutCustomer();
    } catch (error) {
      // Neúspěch odhlášení kvůli síti nesmí zamknout UI v přihlášeném stavu.
      if (!(error instanceof ShopError)) throw error;
    } finally {
      setCustomer(null);
    }
  }, []);

  const value = useMemo(
    () => ({ customer, loading, login, register, logout, refresh }),
    [customer, loading, login, register, logout, refresh],
  );

  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
}

export function useCustomer(): CustomerContextValue {
  const context = useContext(CustomerContext);
  if (context === null) throw new Error('useCustomer musí být uvnitř <CustomerProvider>.');
  return context;
}
